import { useFetcher } from "react-router-dom";
import { XIcon } from "@heroicons/react/solid";

import LoadingIndicator from "components/LoadingIndicator";
import BillingDetailsCard from "v2/pages/billing/billing-details/components/BillingDetailsCard";
import CouponCodeInput from "v2/components/billing/CouponCodeInput";
import SubmitButton from "components/SubmitButton";
import { CouponListElement } from "v2/components/billing/CouponListElement";

import { useUser } from "routes/dataroutes/UserData";
import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { ActiveCouponsDataRoutePath } from "routes/data-routes/billing-data/coupons/active-coupons/route.meta";
import { CheckCouponsFetcherDataType } from "routes/data-routes/billing-data/coupons/check-coupons/route.meta";

import { CouponActions, CouponInputName } from "v2/dataroutes/BillingData";
import { trackPromoCodeApplySubmitted } from "utils/Tracking";
import { CouponError } from "types/Coupons";


function NewCouponInputSection() {
  const user = useUser();
  const { activeCouponsFetcher } = useActiveCoupons();
  const newCouponFetcher = useFetcher<CheckCouponsFetcherDataType>();

  if (!activeCouponsFetcher) {
    return null;
  }

  return (
    <activeCouponsFetcher.Form
      method="POST"
      action={ ActiveCouponsDataRoutePath }
      className="flex gap-x-2.5"
    >
      <CouponCodeInput hideLabel className="flex-1 !min-h-[60px]" inputClassName="h-8" fetcher={ newCouponFetcher } />
      <SubmitButton
        text="Apply"
        className="button button-primary mt-1"
        size="SM"
        fetchersToCheck={ [ activeCouponsFetcher, newCouponFetcher ] }
        disabled={ (newCouponFetcher.state !== "idle") || !!((newCouponFetcher.data?.coupon as CouponError)?.error) || user?.isBlocked }
        showSubmitSpinnerIf={ () => activeCouponsFetcher.formData?.get(CouponActions.add_coupon.name) === CouponActions.add_coupon.value}
        onClick={ () => trackPromoCodeApplySubmitted(newCouponFetcher.data?.coupon?.coupon_code) }
        { ...CouponActions.add_coupon }
      />
    </activeCouponsFetcher.Form>
  );
}

function ActiveCouponsSection() {
  const { activeCoupons, activeCouponsFetcher } = useActiveCoupons();

  if (!activeCoupons || (activeCoupons.length === 0) || !activeCouponsFetcher) {
    return null;
  }

  return (
    <activeCouponsFetcher.Form
      method="POST"
      action={ ActiveCouponsDataRoutePath }
      className="flex flex-col gap-2 pt-2"
    >
      <input type="hidden" { ...CouponActions.remove_coupon } />
      { activeCoupons.map(coupon =>
        <CouponListElement coupon={ coupon } >
          <SubmitButton
            className="button button-text-primary !w-min"
            size="SM"
            icon={ { Icon: XIcon, size: "w-4 h-4", style: "text-neutral-400 hover:text-neutral-900 transition-colors", absolute: false } }
            spinnerTheme="secondary"
            fetchersToCheck={ [ activeCouponsFetcher ] }
            showSubmitSpinnerIf={ () =>
              (activeCouponsFetcher.formData?.get(CouponActions.remove_coupon.name) === CouponActions.remove_coupon.value) &&
              (activeCouponsFetcher.formData?.get(CouponInputName.couponCode) === coupon.coupon_code)
            }
            name={ CouponInputName.couponCode }
            value={ coupon.coupon_code }
          />
        </CouponListElement>
      ) }
    </activeCouponsFetcher.Form>
  );
}

export default function PromoCodeCard() {
  const { activeCouponsFetcher } = useActiveCoupons();

  return (
    <LoadingIndicator fetchers={ [ activeCouponsFetcher ] } animateWhenSubmitting={ false }>
      <BillingDetailsCard
        id="promo-code"
        title="Promo Code"
        subtitle="Apply a promotional code to your subscription"
      >
        <div className="flex flex-col">
          <NewCouponInputSection />
          <ActiveCouponsSection />
        </div>
      </BillingDetailsCard>
    </LoadingIndicator>
  )
}
